import { useState } from 'react';
import { getImageUrl } from '../utils/api';

const CategoryGrid = ({ categories, onCategoryClick }) => {
    const [expandedCategories, setExpandedCategories] = useState({}); // Default all closed

    // Group categories by parent
    const mainCategories = categories
        .filter(c => !c.parent_id)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    
    const getSubcategories = (parentId) => {
        return categories
            .filter(c => c.parent_id === parentId)
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    };

    const handleCategoryClick = (category) => {
        if (onCategoryClick) {
            onCategoryClick(category);
        } else {
            const element = document.getElementById(`category-${category.id}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    };

    const toggleExpanded = (categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: prev[categoryId] === undefined ? true : !prev[categoryId]
        }));
    };

    return (
        <div id="categories-section" className="w-full">
            {/* Main Categories */}
            <div className="space-y-6">
                {mainCategories.map((category, categoryIndex) => {
                    const subcategories = getSubcategories(category.id);
                    const hasSubcategories = subcategories.length > 0;
                    const isExpanded = expandedCategories[category.id] === true; // Default closed

                    return (
                        <div key={category.id}>
                            {/* Main Category Card - Full Width */}
                            <div className="relative">
                                <button
                                    onClick={() => handleCategoryClick(category)}
                                    className="w-full group relative overflow-hidden rounded-2xl hover:shadow-lg transition-all duration-300"
                                    style={{ backgroundColor: 'var(--category-hover-color, #f9fafb)' }}
                                >
                                    {/* Category Image */}
                                    <div className="aspect-[21/9] relative">
                                        {category.image ? (
                                            <img
                                                src={getImageUrl(category.image)}
                                                alt={category.name}
                                                width={840}
                                                height={360}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                loading="lazy"
                                                decoding="async"
                                                fetchPriority={categoryIndex === 0 ? 'high' : 'auto'}
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                                <svg className="w-20 h-20 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                                </svg>
                                            </div>
                                        )}
                                        
                                        {/* Dark Overlay */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                                        
                                        {/* Category Info */}
                                        <div className="absolute bottom-0 left-0 right-0 p-4">
                                            <h3 className="text-white font-bold text-xl md:text-2xl" style={{ fontFamily: 'var(--font-category-title)' }}>
                                                {category.name}
                                            </h3>
                                            {category.description && (
                                                <p className="text-white/80 text-sm mt-1 line-clamp-1" style={{ fontFamily: 'var(--font-category-description)' }}>
                                                    {category.description}
                                                </p>
                                            )}
                                            <div className="flex items-center gap-3 mt-2">
                                                {(category.displayCount !== undefined ? category.displayCount : category.products?.length) > 0 && (
                                                    <span className="text-white/70 text-sm" style={{ fontFamily: 'var(--font-general)' }}>
                                                        {category.displayCount !== undefined ? category.displayCount : category.products.length} ürün
                                                    </span>
                                                )}
                                                {hasSubcategories && (
                                                    <span className="text-white/70 text-sm" style={{ fontFamily: 'var(--font-general)' }}>
                                                        {subcategories.length} alt kategori
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </button>

                                {/* Expand/Collapse Button for Subcategories */}
                                {hasSubcategories && (
                                    <div
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            toggleExpanded(category.id);
                                        }}
                                        className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition z-20 cursor-pointer"
                                    >
                                        <svg 
                                            className={`w-5 h-5 text-gray-700 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                )}
                            </div>

                            {/* Subcategories - Full Width Single Cards */}
                            {hasSubcategories && isExpanded && (
                                <div 
                                    className="mt-3 space-y-3 pl-4 border-l-4"
                                    style={{ borderColor: 'var(--primary-color, #f97316)' }}
                                >
                                    {subcategories.map((subcategory) => (
                                        <button
                                            key={subcategory.id}
                                            onClick={() => handleCategoryClick(subcategory)}
                                            className="w-full group relative overflow-hidden rounded-xl hover:shadow-md transition-all duration-300"
                                            style={{ backgroundColor: 'var(--category-hover-color, #f9fafb)' }}
                                        >
                                            {/* Subcategory Image - Full Width */}
                                            <div className="aspect-[3/1] relative">
                                                {subcategory.image ? (
                                                    <img
                                                        src={getImageUrl(subcategory.image)}
                                                        alt={subcategory.name}
                                                        width={600}
                                                        height={200}
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                        loading="lazy"
                                                        decoding="async"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                                        </svg>
                                                    </div>
                                                )}
                                                
                                                {/* Dark Overlay */}
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                                                
                                                {/* Subcategory Name */}
                                                <div className="absolute bottom-0 left-0 right-0 p-3">
                                                    <h4 className="text-white font-semibold text-base md:text-lg" style={{ fontFamily: 'var(--font-category-title)' }}>
                                                        {subcategory.name}
                                                    </h4>
                                                    {subcategory.products?.length > 0 && (
                                                        <p className="text-white/70 text-sm mt-0.5" style={{ fontFamily: 'var(--font-general)' }}>
                                                            {subcategory.products.length} ürün
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* No Categories */}
            {mainCategories.length === 0 && (
                <div className="text-center py-12">
                    <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                    <p className="text-gray-500 text-lg">Henüz kategori eklenmemiş</p>
                </div>
            )}
        </div>
    );
};

export default CategoryGrid;
